// ApprovalDialog مشترک (AP-IAM-03 §5، IA §4: «تأیید مدیر حاضر یا ارسال درخواست»): دو تب «مدیر حاضر است» (موبایل + PIN
// تأیید شخصی مدیر) و «ارسال درخواست» (دلیل الزامی). فقط نمایشی است: فراخوانی API در اپ (useApproval) انجام می‌شود.
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import { useTranslation } from '@phoenix/i18n';
import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';
import { faDigits, latinDigits } from '../format/digits';
import { isValidMobile, normalizeMobile } from '../format/iranian';
import { ErrorState } from './states';

export type ApprovalDialogTab = 'in_person' | 'request';

export interface ApprovalInPersonInput {
  approverMobile: string;
  pin: string;
}

export interface ApprovalDialogProps {
  open: boolean;
  /** شرح عملیات و سقف (مثلاً «تخفیف ۲۰٪ — سقف شما ۱۵٪»). */
  summary?: ReactNode;
  busy?: boolean;
  /** خطای آخرین اقدام (ApiError: PHX-IAM-0601، 0602، 0603، 0605). */
  error?: unknown;
  /** درخواست ناهمگام ثبت شد و منتظر تصمیم مدیر است. */
  requested?: boolean;
  /** تب «ارسال درخواست» (پیش‌فرض فعال). */
  allowRequest?: boolean;
  onApproveInPerson: (input: ApprovalInPersonInput) => void;
  onRequest?: (reason: string) => void;
  onCancel: () => void;
}

const PIN_PATTERN = /^\d{4,8}$/;

export function ApprovalDialog({
  open,
  summary,
  busy = false,
  error,
  requested = false,
  allowRequest = true,
  onApproveInPerson,
  onRequest,
  onCancel,
}: ApprovalDialogProps) {
  const { t } = useTranslation('common');
  const titleId = useId();
  const [tab, setTab] = useState<ApprovalDialogTab>('in_person');
  const [mobile, setMobile] = useState('');
  const [pin, setPin] = useState('');
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setTab('in_person');
      setMobile('');
      setPin('');
      setReason('');
      setTouched(false);
    }
  }, [open]);

  useEffect(() => {
    if (requested) {
      setTab('request');
    }
  }, [requested]);

  const normalizedMobile = normalizeMobile(mobile);
  const mobileInvalid = !isValidMobile(mobile);
  const pinValue = latinDigits(pin).trim();
  const pinInvalid = !PIN_PATTERN.test(pinValue);
  const reasonInvalid = reason.trim().length < 3;
  const showRequest = allowRequest && onRequest !== undefined;

  const submit = (event?: FormEvent) => {
    event?.preventDefault();
    setTouched(true);
    if (tab === 'in_person') {
      if (!mobileInvalid && !pinInvalid && normalizedMobile) {
        onApproveInPerson({ approverMobile: normalizedMobile, pin: pinValue });
      }
    } else if (!reasonInvalid && onRequest) {
      onRequest(reason.trim());
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onCancel}
      aria-labelledby={titleId}
      maxWidth="xs"
      fullWidth
      data-testid="approval-dialog"
    >
      <Box component="form" noValidate onSubmit={submit}>
        <DialogTitle id={titleId}>{t('common:approval.title')}</DialogTitle>
        <DialogContent>
          {summary ? (
            <Alert severity="warning" icon={false} sx={{ mb: 2 }} data-testid="approval-summary">
              {summary}
            </Alert>
          ) : null}
          {showRequest && (
            <Tabs
              value={tab}
              onChange={(_, value: ApprovalDialogTab) => {
                setTab(value);
                setTouched(false);
              }}
              variant="fullWidth"
              sx={{ mb: 2 }}
            >
              <Tab value="in_person" label={t('common:approval.in_person')} data-testid="approval-tab-in-person" />
              <Tab value="request" label={t('common:approval.request')} data-testid="approval-tab-request" />
            </Tabs>
          )}
          {tab === 'in_person' ? (
            <Stack spacing={2}>
              <TextField
                required
                autoFocus
                label={t('common:approval.mobile')}
                value={mobile}
                onChange={(event) => setMobile(event.target.value)}
                error={touched && mobileInvalid}
                helperText={touched && mobileInvalid ? t('common:validation.mobile') : ' '}
                slotProps={{
                  htmlInput: { inputMode: 'tel', dir: 'ltr', autoComplete: 'off', 'data-testid': 'approval-mobile' },
                }}
              />
              <TextField
                required
                type="password"
                label={t('common:approval.pin')}
                value={pin}
                onChange={(event) => setPin(event.target.value.slice(0, 8))}
                error={touched && pinInvalid}
                helperText={touched && pinInvalid ? t('common:approval.pin_required') : ' '}
                slotProps={{
                  htmlInput: {
                    inputMode: 'numeric',
                    dir: 'ltr',
                    autoComplete: 'off',
                    maxLength: 8,
                    'data-testid': 'approval-pin',
                  },
                }}
              />
            </Stack>
          ) : requested ? (
            <Alert severity="info" data-testid="approval-pending">
              {t('common:approval.pending')}
            </Alert>
          ) : (
            <Stack spacing={1}>
              <TextField
                required
                multiline
                minRows={2}
                label={t('common:approval.reason')}
                value={reason}
                onChange={(event) => setReason(event.target.value.slice(0, 500))}
                error={touched && reasonInvalid}
                helperText={touched && reasonInvalid ? t('common:dialogs.reason_min', { min: faDigits(3) }) : ' '}
                slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'approval-reason' } }}
              />
              <Alert severity="info" icon={false}>
                {t('common:approval.request_hint')}
              </Alert>
            </Stack>
          )}
          {error ? <ErrorState error={error} compact /> : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} disabled={busy}>
            {t(requested ? 'common:actions.close' : 'common:actions.cancel')}
          </Button>
          {!(tab === 'request' && requested) && (
            <Button type="submit" variant="contained" disabled={busy} data-testid="approval-submit">
              {t(tab === 'in_person' ? 'common:approval.approve' : 'common:approval.send')}
            </Button>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
}
